/**
 * Scope expansion, mirroring `expand_scopes` in `backend/efss/auth.py` (SPEC.md §2.2).
 *
 * The server is the authority — it decides what a token can actually do. This copy exists
 * only so the settings page can *show* what a token reaches, and so it can warn before you
 * switch this browser to one that cannot get back to the page. Keep the two in step; the
 * table is four entries and both sides have tests on it.
 */

/** What each scope grants beyond itself. `capture` is absent: it implies nothing.
 *
 * Drifted from the backend's own `IMPLIED_SCOPES` (`backend/efss/auth.py`) for a while:
 * this said `write` implies only `read`, when the server has always expanded it to both
 * `read` and `capture` (`test_auth.py::test_scope_expansion_is_transitive` asserts exactly
 * that). Caught displaying a watch's `write`-scoped token as "(+read)" instead of the
 * true "(+read, capture)" — this file's own docstring warns that a mirror that drifts is
 * worse than no mirror, and this was that. */
const IMPLIES: Record<string, string[]> = {
  admin: ["write", "read"],
  write: ["read", "capture"],
};

/** Granted scopes plus everything they imply, applied until it stops growing. */
export function effectiveScopes(granted: string[]): string[] {
  const out = new Set(granted);
  let changed = true;
  while (changed) {
    changed = false;
    for (const scope of [...out]) {
      for (const extra of IMPLIES[scope] ?? []) {
        if (!out.has(extra)) {
          out.add(extra);
          changed = true;
        }
      }
    }
  }
  return [...out];
}

/** Scopes a token reaches that were not explicitly granted. */
export function impliedScopes(granted: string[]): string[] {
  return effectiveScopes(granted).filter((scope) => !granted.includes(scope));
}
